import { format } from "date-fns";
import { Alert, Badge, Button, Icon, Panel, Skeleton, Typography } from "@/components/atoms";
import { useProfile } from "@/hooks";
import { useGetVeivoVehicleDocumentsQuery } from "@/redux/apis/vehicle/veivoApi";
import { clsx } from "@/utils";
import { useViewVehicleContext } from "../../context";
import { buildVeivoDocumentRows, needsAttention, VeivoDocumentRow, VeivoDocumentState } from "./veivoDocumentState";

const BADGES: Record<VeivoDocumentState, { type: "success" | "warning" | "danger" | "disabled"; message: string }> = {
  verified: { type: "success", message: "Verified" },
  expiring: { type: "warning", message: "Expiring soon" },
  expired: { type: "danger", message: "Expired" },
  missing: { type: "danger", message: "Missing" },
  "not-provided": { type: "disabled", message: "Not provided" },
};

interface VeivoDocumentsPanelProps {
  /** Renew one or more documents through Veivo (DEV-1732). Omitted = read only. */
  onRenew?: (docTypes: string[]) => void;
}

/**
 * The vehicle's documents as Veivo verified them (DEV-1731): the country's own
 * list (a GB vehicle shows its V5C, not a generic "registration"), with each
 * document's image, status and expiry. Replaces the hardcoded registration /
 * insurance / accreditation cards for KYV vehicles.
 */
export const VeivoDocumentsPanel = ({ onRenew }: VeivoDocumentsPanelProps) => {
  const { vehicle } = useViewVehicleContext();
  const { data, isFetching, isError } = useGetVeivoVehicleDocumentsQuery(vehicle.uuid);

  if (isFetching && !data) {
    return (
      <Panel>
        <Skeleton />
      </Panel>
    );
  }
  if (isError || !data) {
    return (
      <Panel>
        <Typography variant="h3">Verified Documents</Typography>
        <Typography className="mt-2 text-neutral-dark-gray">The verified documents could not be loaded. Refresh to try again.</Typography>
      </Panel>
    );
  }

  const rows = buildVeivoDocumentRows(data, vehicle);
  const attention = needsAttention(rows);
  const renewing = data.pendingValidation !== null;

  return (
    <Panel>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <Typography variant="h3">Verified Documents</Typography>
          <Typography className="text-neutral-dark-gray">
            {data.country
              ? `The documents required for vehicles in ${data.country}, checked by vehicle verification`
              : "Checked by vehicle verification"}
          </Typography>
        </div>
        {renewing && <Badge type="info" message="Renewal in progress" />}
      </div>

      {attention.length > 0 && (
        <Alert
          type={attention.some((row) => row.state === "expired") ? "danger" : "warning"}
          className="mb-4"
          message={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                {attention.map((row) => `${row.required.name} ${row.state === "expired" ? "has expired" : "expires soon"}`).join(". ")}.
                {" Renew it to keep this vehicle verified."}
              </span>
              {onRenew && !renewing && (
                <Button variant="primary" size="sm" onClick={() => onRenew(attention.map((row) => row.required.docType))}>
                  Renew
                </Button>
              )}
            </div>
          }
        />
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((row) => (
          <VeivoDocumentCard key={row.required.docType} row={row} onRenew={onRenew && !renewing ? onRenew : undefined} />
        ))}
      </div>
    </Panel>
  );
};

const VeivoDocumentCard = ({ row, onRenew }: { row: VeivoDocumentRow; onRenew?: (docTypes: string[]) => void }) => {
  const { dateFormat } = useProfile();
  const badge = BADGES[row.state];
  const image = row.document?.imageUrl;

  return (
    <div
      className={clsx(
        "flex flex-col overflow-hidden rounded-lg border bg-neutral-surface-gray",
        row.state === "expired" || row.state === "missing"
          ? "border-danger"
          : row.state === "expiring"
          ? "border-warning"
          : "border-neutral-mid-gray"
      )}
      data-testid={`veivo-document-${row.required.docType}`}
    >
      <div className="flex h-32 items-center justify-center bg-neutral-gray">
        {image ? (
          <a href={image} target="_blank" rel="noreferrer" className="h-full w-full">
            <img src={image} alt={row.required.name} className="h-full w-full object-cover" />
          </a>
        ) : (
          <Icon name="DocumentText" variant="Bold" size="xl" className="text-neutral-dark-gray" />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge type={badge.type} message={badge.message} size="sm" />
          {row.document?.isTest && <Badge type="disabled" message="Test data" size="sm" />}
          {row.required.optional && <Typography className="text-neutral-dark-gray">Optional</Typography>}
        </div>
        <Typography variant="action" className="text-neutral-black">
          {row.required.name}
        </Typography>
        <Typography className="text-neutral-dark-gray">
          {row.expiryDate
            ? `${row.state === "expired" ? "Expired on" : "Expires on"} ${format(new Date(row.expiryDate), dateFormat)}`
            : row.document
            ? "No expiry date"
            : row.required.description || "Not provided yet"}
        </Typography>
        {onRenew && (
          <div className="mt-auto pt-2">
            <Button variant="tertiary" size="sm" onClick={() => onRenew([row.required.docType])}>
              {row.document ? "Renew" : "Add"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
